import { createContext, useContext, useEffect, useState } from "react";

export const LANGS = [
  { code: "en", label: "EN", name: "English" },
  { code: "es", label: "ES", name: "Español" },
  { code: "pt", label: "PT", name: "Português" },
];

export const translations = {
  en: {
    nav: { whatWeDo: "What We Do", pricing: "Pricing", process: "Process", contact: "Contact", getStarted: "Get Started" },
    hero: {
      badge: "Websites for local businesses",
      line1: "Your Business",
      line2: "Deserves to be",
      line3: "Found Online.",
      paragraph: "Modern, professional websites that put small local businesses on the map — built fast, priced fair, and supported around the clock.",
      getStarted: "Get Started",
      hours: "Open 24/7",
      call: "Call us anytime —",
      statNote: "website, live & ready",
    },
    marquee: ["MODERN", "VIGOROUS", "LOCALNOVA", "OPEN 24/7", "FOUND ONLINE", "LOCAL FIRST"],
    whatwedo: {
      eyebrow: "/ What We Do",
      heading: "We help small local businesses get online.",
      paragraph: "Professional, modern websites that showcase your services and attract new customers — without the agency price tag or the tech overwhelm.",
      services: [
        { title: "Custom Web Design", desc: "Modern, mobile-friendly websites designed around your brand — built to showcase your services and win trust." },
        { title: "Setup & Publishing", desc: "We handle configuration, domain setup and publishing, so your business goes live without the technical headache." },
        { title: "Ongoing Support", desc: "Backups, security, updates and quick fixes — we keep your site running smoothly while you run your business." },
      ],
      bannerA: "Your customers are searching.",
      bannerB: "Let them find you.",
    },
    pricing: {
      eyebrow: "/ Pricing",
      heading: "Simple, honest pricing.",
      sub: "Launch once, then choose the support that fits how fast you want to grow.",
      mostPopular: "Most Popular",
      cta: "Get Started",
      plans: [
        { name: "Website Setup", cadence: "one-time", blurb: "Everything you need to launch.", features: ["Custom website design & development", "Configuration & publishing", "Mobile-friendly responsive design", "Domain registration fee not included (or client provides domain)"] },
        { name: "Website Care Plan", cadence: "/month", blurb: "Keep your website running smoothly.", features: ["Website hosting", "SSL certificate (secure site)", "Regular backups", "Basic email support", "Small updates & fixes"] },
        { name: "Growth Plan", cadence: "/month", blurb: "Everything in the Website Care Plan, plus:", features: ["3–5 content changes per month", "Add photos, update text & services", "Update business hours & info", "Add small new pages", "Faster priority support"] },
      ],
    },
    process: {
      eyebrow: "/ How It Works",
      heading: "Three steps to online.",
      steps: [
        { title: "Contact Us", desc: "Tell us about your business and what you need. A quick chat is all it takes to get started." },
        { title: "We Build Your Site", desc: "We design and develop a modern, mobile-friendly website tailored to your services and brand." },
        { title: "Go Live", desc: "We configure, publish and launch your site — then keep it running smoothly, 24/7." },
      ],
    },
    contact: {
      eyebrow: "/ Contact",
      heading: "Ready to get started?",
      paragraph: "Send us a message and let's get your business found online. We reply fast — we're open 24/7.",
      name: "Name", email: "Email", message: "Message",
      namePh: "Your name", emailPh: "you@business.com", messagePh: "Tell us about your business and what you need...",
      whatsapp: "WhatsApp", whatsappPh: "(555) 123-4567",
      submit: "Send Us a Message", sending: "Sending...", whatsappBtn: "Send via WhatsApp", or: "or",
      invalidEmail: "Please enter a valid email address.",
      invalidWhatsapp: "Please enter a complete phone number.",
      infoCall: "Call us", infoEmail: "Email", infoHours: "Availability", hours: "Open 24/7",
      success: "Message sent! We'll be in touch shortly.",
      error: "Something went wrong. Please try again or call us.",
      empty: "Please fill in all fields.",
    },
    footer: {
      tagline: "Modern websites for small local businesses — designed, built and supported so you can be found online.",
      explore: "Explore", getInTouch: "Get in touch", hours: "Open 24/7",
      rights: "All rights reserved.", motto: "Modern · Vigorous · Local",
      privacy: "Privacy Policy", terms: "Terms of Use",
    },
    install: { title: "Install LocalNova", desc: "Add our app to your device for one-tap access — it works offline too.", button: "Install App", later: "Not now" },
    notfound: { code: "404", title: "This page went off the grid.", desc: "The page you're looking for doesn't exist, moved, or never made it online. Let's get you back on track.", button: "Back to Home" },
  },
  es: {
    nav: { whatWeDo: "Qué Hacemos", pricing: "Precios", process: "Proceso", contact: "Contacto", getStarted: "Empezar" },
    hero: {
      badge: "Sitios web para negocios locales",
      line1: "Tu Negocio",
      line2: "Merece Ser",
      line3: "Encontrado Online.",
      paragraph: "Sitios web modernos y profesionales que ponen a los pequeños negocios locales en el mapa — hechos rápido, a precio justo y con soporte las 24 horas.",
      getStarted: "Empezar",
      hours: "Abierto 24/7",
      call: "Llámanos cuando quieras —",
      statNote: "sitio web, listo y en vivo",
    },
    marquee: ["MODERNO", "POTENTE", "LOCALNOVA", "ABIERTO 24/7", "ENCONTRADO ONLINE", "LO LOCAL PRIMERO"],
    whatwedo: {
      eyebrow: "/ Qué Hacemos",
      heading: "Ayudamos a los pequeños negocios locales a estar online.",
      paragraph: "Sitios web modernos y profesionales que muestran tus servicios y atraen nuevos clientes — sin el precio de una agencia ni los dolores de cabeza técnicos.",
      services: [
        { title: "Diseño Web a Medida", desc: "Sitios web modernos y adaptados a móviles, diseñados en torno a tu marca — para mostrar tus servicios y generar confianza." },
        { title: "Configuración y Publicación", desc: "Nos encargamos de la configuración, el dominio y la publicación, para que tu negocio salga online sin complicaciones técnicas." },
        { title: "Soporte Continuo", desc: "Copias de seguridad, seguridad, actualizaciones y arreglos rápidos — mantenemos tu sitio funcionando mientras tú diriges tu negocio." },
      ],
      bannerA: "Tus clientes están buscando.",
      bannerB: "Deja que te encuentren.",
    },
    pricing: {
      eyebrow: "/ Precios",
      heading: "Precios simples y honestos.",
      sub: "Lanza una vez y luego elige el soporte que se adapte a la rapidez con la que quieras crecer.",
      mostPopular: "Más Popular",
      cta: "Empezar",
      plans: [
        { name: "Creación del Sitio", cadence: "pago único", blurb: "Todo lo que necesitas para lanzar.", features: ["Diseño y desarrollo web a medida", "Configuración y publicación", "Diseño responsive para móviles", "Registro de dominio no incluido (o lo aporta el cliente)"] },
        { name: "Plan de Mantenimiento", cadence: "/mes", blurb: "Mantén tu sitio funcionando sin problemas.", features: ["Hosting del sitio web", "Certificado SSL (sitio seguro)", "Copias de seguridad periódicas", "Soporte básico por correo", "Pequeñas actualizaciones y arreglos"] },
        { name: "Plan de Crecimiento", cadence: "/mes", blurb: "Todo lo del Plan de Mantenimiento, más:", features: ["3–5 cambios de contenido al mes", "Añadir fotos, actualizar textos y servicios", "Actualizar horarios e información", "Añadir pequeñas páginas nuevas", "Soporte prioritario más rápido"] },
      ],
    },
    process: {
      eyebrow: "/ Cómo Funciona",
      heading: "Tres pasos para estar online.",
      steps: [
        { title: "Contáctanos", desc: "Cuéntanos sobre tu negocio y lo que necesitas. Una charla rápida es todo para empezar." },
        { title: "Creamos Tu Sitio", desc: "Diseñamos y desarrollamos un sitio web moderno y adaptado a móviles, a la medida de tus servicios y tu marca." },
        { title: "Salimos en Vivo", desc: "Configuramos, publicamos y lanzamos tu sitio — y lo mantenemos funcionando 24/7." },
      ],
    },
    contact: {
      eyebrow: "/ Contacto",
      heading: "¿Listo para empezar?",
      paragraph: "Envíanos un mensaje y hagamos que tu negocio se encuentre online. Respondemos rápido — estamos abiertos 24/7.",
      name: "Nombre", email: "Correo", message: "Mensaje",
      namePh: "Tu nombre", emailPh: "tu@negocio.com", messagePh: "Cuéntanos sobre tu negocio y lo que necesitas...",
      whatsapp: "WhatsApp", whatsappPh: "(555) 123-4567",
      submit: "Envíanos un Mensaje", sending: "Enviando...", whatsappBtn: "Enviar por WhatsApp", or: "o",
      invalidEmail: "Introduce un correo electrónico válido.",
      invalidWhatsapp: "Introduce un número de teléfono completo.",
      infoCall: "Llámanos", infoEmail: "Correo", infoHours: "Disponibilidad", hours: "Abierto 24/7",
      success: "¡Mensaje enviado! Nos pondremos en contacto pronto.",
      error: "Algo salió mal. Inténtalo de nuevo o llámanos.",
      empty: "Por favor completa todos los campos.",
    },
    footer: {
      tagline: "Sitios web modernos para pequeños negocios locales — diseñados, construidos y con soporte para que te encuentren online.",
      explore: "Explorar", getInTouch: "Contacto", hours: "Abierto 24/7",
      rights: "Todos los derechos reservados.", motto: "Moderno · Potente · Local",
      privacy: "Política de Privacidad", terms: "Términos de Uso",
    },
    install: { title: "Instala LocalNova", desc: "Añade nuestra app a tu dispositivo para acceder con un toque — también funciona sin conexión.", button: "Instalar App", later: "Ahora no" },
    notfound: { code: "404", title: "Esta página se salió del mapa.", desc: "La página que buscas no existe, se movió o nunca llegó a estar online. Volvamos al camino.", button: "Volver al Inicio" },
  },
  pt: {
    nav: { whatWeDo: "O Que Fazemos", pricing: "Preços", process: "Processo", contact: "Contato", getStarted: "Começar" },
    hero: {
      badge: "Sites para negócios locais",
      line1: "Seu Negócio",
      line2: "Merece Ser",
      line3: "Encontrado Online.",
      paragraph: "Sites modernos e profissionais que colocam os pequenos negócios locais no mapa — feitos rápido, com preço justo e suporte 24 horas.",
      getStarted: "Começar",
      hours: "Aberto 24/7",
      call: "Ligue a qualquer hora —",
      statNote: "site, no ar e pronto",
    },
    marquee: ["MODERNO", "VIGOROSO", "LOCALNOVA", "ABERTO 24/7", "ENCONTRADO ONLINE", "LOCAL EM 1º LUGAR"],
    whatwedo: {
      eyebrow: "/ O Que Fazemos",
      heading: "Ajudamos pequenos negócios locais a estarem online.",
      paragraph: "Sites modernos e profissionais que mostram seus serviços e atraem novos clientes — sem o preço de agência nem a dor de cabeça técnica.",
      services: [
        { title: "Design de Site Sob Medida", desc: "Sites modernos e responsivos, criados em torno da sua marca — para mostrar seus serviços e conquistar confiança." },
        { title: "Configuração e Publicação", desc: "Cuidamos da configuração, do domínio e da publicação, para o seu negócio entrar no ar sem complicação técnica." },
        { title: "Suporte Contínuo", desc: "Backups, segurança, atualizações e correções rápidas — mantemos seu site funcionando enquanto você cuida do negócio." },
      ],
      bannerA: "Seus clientes estão procurando.",
      bannerB: "Deixe que encontrem você.",
    },
    pricing: {
      eyebrow: "/ Preços",
      heading: "Preços simples e honestos.",
      sub: "Lance uma vez e depois escolha o suporte que combina com a velocidade com que você quer crescer.",
      mostPopular: "Mais Popular",
      cta: "Começar",
      plans: [
        { name: "Criação do Site", cadence: "pagamento único", blurb: "Tudo o que você precisa para lançar.", features: ["Design e desenvolvimento sob medida", "Configuração e publicação", "Design responsivo para celular", "Registro de domínio não incluído (ou o cliente fornece o domínio)"] },
        { name: "Plano de Manutenção", cadence: "/mês", blurb: "Mantenha seu site funcionando sem problemas.", features: ["Hospedagem do site", "Certificado SSL (site seguro)", "Backups regulares", "Suporte básico por e-mail", "Pequenas atualizações e correções"] },
        { name: "Plano de Crescimento", cadence: "/mês", blurb: "Tudo do Plano de Manutenção, mais:", features: ["3–5 alterações de conteúdo por mês", "Adicionar fotos, atualizar textos e serviços", "Atualizar horários e informações", "Adicionar pequenas páginas novas", "Suporte prioritário mais rápido"] },
      ],
    },
    process: {
      eyebrow: "/ Como Funciona",
      heading: "Três passos para estar online.",
      steps: [
        { title: "Fale Conosco", desc: "Conte sobre o seu negócio e o que você precisa. Uma conversa rápida já é o suficiente para começar." },
        { title: "Criamos Seu Site", desc: "Projetamos e desenvolvemos um site moderno e responsivo, sob medida para seus serviços e sua marca." },
        { title: "Entrar no Ar", desc: "Configuramos, publicamos e lançamos seu site — e o mantemos funcionando 24/7." },
      ],
    },
    contact: {
      eyebrow: "/ Contato",
      heading: "Pronto para começar?",
      paragraph: "Envie uma mensagem e vamos fazer seu negócio ser encontrado online. Respondemos rápido — estamos abertos 24/7.",
      name: "Nome", email: "E-mail", message: "Mensagem",
      namePh: "Seu nome", emailPh: "voce@negocio.com", messagePh: "Conte sobre o seu negócio e o que você precisa...",
      whatsapp: "WhatsApp", whatsappPh: "(555) 123-4567",
      submit: "Envie uma Mensagem", sending: "Enviando...", whatsappBtn: "Enviar pelo WhatsApp", or: "ou",
      invalidEmail: "Digite um e-mail válido.",
      invalidWhatsapp: "Digite um número de telefone completo.",
      infoCall: "Ligue", infoEmail: "E-mail", infoHours: "Disponibilidade", hours: "Aberto 24/7",
      success: "Mensagem enviada! Entraremos em contato em breve.",
      error: "Algo deu errado. Tente novamente ou ligue para nós.",
      empty: "Por favor, preencha todos os campos.",
    },
    footer: {
      tagline: "Sites modernos para pequenos negócios locais — projetados, construídos e com suporte para você ser encontrado online.",
      explore: "Explorar", getInTouch: "Fale conosco", hours: "Aberto 24/7",
      rights: "Todos os direitos reservados.", motto: "Moderno · Vigoroso · Local",
      privacy: "Política de Privacidade", terms: "Termos de Uso",
    },
    install: { title: "Instale o LocalNova", desc: "Adicione nosso app ao seu dispositivo para acesso rápido — funciona offline também.", button: "Instalar App", later: "Agora não" },
    notfound: { code: "404", title: "Esta página saiu do ar.", desc: "A página que você procura não existe, foi movida ou nunca chegou a ficar online. Vamos te colocar de volta no caminho.", button: "Voltar para a Página Inicial" },
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window !== "undefined") return localStorage.getItem("ln_lang") || "en";
    return "en";
  });
  useEffect(() => {
    localStorage.setItem("ln_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const t = translations[lang];
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
